import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type CreateShipmentResponse,
  type PatchShipmentResponse,
  type ProformaInvoice,
  type Shipment,
  type ShipmentStatus,
} from '@/lib/api';
import { useModalDismiss } from '@/lib/use-modal-dismiss';

interface Props {
  open: boolean;
  onClose: () => void;
  proforma: ProformaInvoice;
  rfqId?: string;
  /** Optional: when editing an existing shipment, the modal opens in PATCH mode. */
  shipment?: Shipment | null;
}

const CARRIER_OPTIONS = ['FedEx', 'UPS', 'DHL', 'Kuehne+Nagel', 'TNT', 'Lufthansa Cargo', 'Other'];

export function ShipmentModal({ open, onClose, proforma, rfqId, shipment }: Props) {
  const dismiss = useModalDismiss(onClose);
  const { getToken } = useAuth();
  const qc = useQueryClient();

  const isEdit = Boolean(shipment);

  const [vendorPoRef, setVendorPoRef] = useState('');
  const [carrier, setCarrier] = useState('FedEx');
  const [awbOrTracking, setAwbOrTracking] = useState('');
  const [aesItn, setAesItn] = useState('');
  const [shipDate, setShipDate] = useState('');
  const [estimatedDeliveryAt, setEstimatedDeliveryAt] = useState('');
  const [grossWeightKg, setGrossWeightKg] = useState('');
  const [netWeightKg, setNetWeightKg] = useState('');
  const [packageCount, setPackageCount] = useState<number | ''>(1);
  const [packageDimensions, setPackageDimensions] = useState('');
  const [shipToAddress, setShipToAddress] = useState('');
  const [shipToCarrierAccount, setShipToCarrierAccount] = useState('');
  const [notes, setNotes] = useState('');
  // Shipping before the proforma is paid is allowed, but only as a deliberate,
  // recorded choice (customer on terms, or a logistics partner booking ahead).
  const unpaid = proforma.status !== 'paid';
  const [shipBeforePayment, setShipBeforePayment] = useState(false);

  const [saved, setSaved] = useState<Shipment | null>(shipment ?? null);

  useEffect(() => {
    if (!open) {
      setVendorPoRef('');
      setCarrier('FedEx');
      setAwbOrTracking('');
      setAesItn('');
      setShipDate('');
      setEstimatedDeliveryAt('');
      setGrossWeightKg('');
      setNetWeightKg('');
      setPackageCount(1);
      setPackageDimensions('');
      setShipToAddress('');
      setShipToCarrierAccount('');
      setNotes('');
      setSaved(null);
      return;
    }
    if (shipment) {
      setSaved(shipment);
      setVendorPoRef(shipment.vendorPoRef ?? '');
      setCarrier(shipment.carrier ?? 'FedEx');
      setAwbOrTracking(shipment.awbOrTracking ?? '');
      setAesItn(shipment.aesItn ?? '');
      setShipDate(shipment.shipDate ? shipment.shipDate.slice(0, 10) : '');
      setEstimatedDeliveryAt(shipment.estimatedDeliveryAt ? shipment.estimatedDeliveryAt.slice(0, 10) : '');
      setGrossWeightKg(shipment.grossWeightKg ?? '');
      setNetWeightKg(shipment.netWeightKg ?? '');
      setPackageCount(shipment.packageCount ?? 1);
      setPackageDimensions(shipment.packageDimensions ?? '');
      setShipToAddress(shipment.shipToAddress ?? '');
      setShipToCarrierAccount(shipment.shipToCarrierAccount ?? '');
      setNotes(shipment.notes ?? '');
    }
  }, [open, shipment]);

  function invalidateLists() {
    qc.invalidateQueries({ queryKey: ['shipments'] });
    qc.invalidateQueries({ queryKey: ['proforma-invoices'] });
    if (rfqId) qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
    qc.invalidateQueries({ queryKey: ['rfqs'] });
  }

  function buildBody() {
    const body: Record<string, unknown> = {
      proformaInvoicePublicId: proforma.id,
    };
    if (vendorPoRef) body.vendorPoRef = vendorPoRef;
    if (carrier) body.carrier = carrier;
    if (awbOrTracking) body.awbOrTracking = awbOrTracking;
    if (aesItn) body.aesItn = aesItn;
    if (shipDate) body.shipDate = new Date(shipDate).toISOString();
    if (estimatedDeliveryAt) body.estimatedDeliveryAt = new Date(estimatedDeliveryAt).toISOString();
    if (grossWeightKg) body.grossWeightKg = parseFloat(grossWeightKg);
    if (netWeightKg) body.netWeightKg = parseFloat(netWeightKg);
    if (typeof packageCount === 'number') body.packageCount = packageCount;
    if (packageDimensions) body.packageDimensions = packageDimensions;
    if (shipToAddress) body.shipToAddress = shipToAddress;
    if (shipToCarrierAccount) body.shipToCarrierAccount = shipToCarrierAccount;
    if (notes) body.notes = notes;
    if (unpaid && shipBeforePayment) body.shipBeforePayment = true;
    return body;
  }

  const createMutation = useMutation({
    mutationFn: async () => {
      return api<CreateShipmentResponse>('/api/shipments', {
        method: 'POST',
        tokenGetter: () => getToken(),
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify(buildBody()),
      });
    },
    onSuccess: (data) => {
      setSaved(data.shipment);
      invalidateLists();
    },
  });

  const patchFieldsMutation = useMutation({
    mutationFn: async () => {
      if (!saved) throw new Error('No saved shipment');
      const body = buildBody();
      delete (body as { proformaInvoicePublicId?: string }).proformaInvoicePublicId;
      return api<PatchShipmentResponse>(`/api/shipments/${saved.id}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify(body),
      });
    },
    onSuccess: (data) => {
      setSaved(data.shipment);
      invalidateLists();
    },
  });

  const patchStatusMutation = useMutation({
    mutationFn: async (newStatus: ShipmentStatus) => {
      if (!saved) throw new Error('No saved shipment');
      return api<PatchShipmentResponse>(`/api/shipments/${saved.id}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify({ status: newStatus }),
      });
    },
    onSuccess: (data) => {
      setSaved(data.shipment);
      invalidateLists();
    },
  });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      {...dismiss}
    >
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[94dvh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="font-semibold">{isEdit ? 'Update shipment' : 'Record vendor PO + shipment'}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Once saved, the line moves to <span className="font-medium">Shipped</span> and a GHL note is filed.
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm" aria-label="Close">✕</button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4 bg-muted/10">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            For proforma <span className="font-mono text-foreground">{proforma.ref}</span>
            {saved && (
              <>
                {' · '}Status <span className="font-semibold text-foreground">{saved.status}</span>
                {saved.ref && <> · ref <span className="font-mono text-foreground">{saved.ref}</span></>}
              </>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-medium text-muted-foreground mb-1">Vendor PO #</div>
              <input
                value={vendorPoRef}
                onChange={(e) => setVendorPoRef(e.target.value)}
                placeholder="VPO-..."
                maxLength={120}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
              />
            </label>
            <label className="block">
              <div className="text-xs font-medium text-muted-foreground mb-1">Carrier</div>
              <select
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
              >
                {CARRIER_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label className="block col-span-2">
              <div className="text-xs font-medium text-muted-foreground mb-1">AWB / Tracking #</div>
              <input
                value={awbOrTracking}
                onChange={(e) => setAwbOrTracking(e.target.value)}
                placeholder="123-12345678"
                maxLength={120}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
              />
            </label>
            <label className="block col-span-2">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                AES ITN <span className="text-[10px] uppercase ml-1 text-muted-foreground">(US export &gt; $2,500)</span>
              </div>
              <input
                value={aesItn}
                onChange={(e) => setAesItn(e.target.value)}
                placeholder="X20231201123456"
                maxLength={80}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
              />
            </label>
            <label className="block">
              <div className="text-xs font-medium text-muted-foreground mb-1">Ship date</div>
              <input
                type="date"
                value={shipDate}
                onChange={(e) => setShipDate(e.target.value)}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
              />
            </label>
            <label className="block">
              <div className="text-xs font-medium text-muted-foreground mb-1">Est. delivery</div>
              <input
                type="date"
                value={estimatedDeliveryAt}
                onChange={(e) => setEstimatedDeliveryAt(e.target.value)}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
              />
            </label>
            <label className="block">
              <div className="text-xs font-medium text-muted-foreground mb-1">Gross weight (kg)</div>
              <input
                type="number"
                step="0.001"
                min="0"
                value={grossWeightKg}
                onChange={(e) => setGrossWeightKg(e.target.value)}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
              />
            </label>
            <label className="block">
              <div className="text-xs font-medium text-muted-foreground mb-1">Net weight (kg)</div>
              <input
                type="number"
                step="0.001"
                min="0"
                value={netWeightKg}
                onChange={(e) => setNetWeightKg(e.target.value)}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
              />
            </label>
            <label className="block">
              <div className="text-xs font-medium text-muted-foreground mb-1">Packages</div>
              <input
                type="number"
                min="1"
                value={packageCount}
                onChange={(e) => setPackageCount(e.target.value === '' ? '' : Number(e.target.value))}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
              />
            </label>
            <label className="block">
              <div className="text-xs font-medium text-muted-foreground mb-1">Dimensions</div>
              <input
                value={packageDimensions}
                onChange={(e) => setPackageDimensions(e.target.value)}
                placeholder="40×30×25 cm"
                maxLength={200}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
              />
            </label>
            <label className="block col-span-2">
              <div className="text-xs font-medium text-muted-foreground mb-1">Ship-to address</div>
              <textarea
                value={shipToAddress}
                onChange={(e) => setShipToAddress(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Customer's consignee address"
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
              />
            </label>
            <label className="block col-span-2">
              <div className="text-xs font-medium text-muted-foreground mb-1">Ship-to carrier account #</div>
              <input
                value={shipToCarrierAccount}
                onChange={(e) => setShipToCarrierAccount(e.target.value)}
                maxLength={120}
                placeholder="If shipping on customer's account"
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded font-mono"
              />
            </label>
            <label className="block col-span-2">
              <div className="text-xs font-medium text-muted-foreground mb-1">Notes</div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                maxLength={2000}
                className="w-full px-2 py-1.5 text-sm bg-background border border-border rounded"
              />
            </label>
          </div>

          {createMutation.isError && (
            <div className="text-sm text-destructive">
              {(createMutation.error as { message?: string })?.message ?? 'Save failed'}
            </div>
          )}
          {patchFieldsMutation.isError && (
            <div className="text-sm text-destructive">
              {(patchFieldsMutation.error as { message?: string })?.message ?? 'Update failed'}
            </div>
          )}
          {patchStatusMutation.isError && (
            <div className="text-sm text-destructive">
              {(patchStatusMutation.error as { message?: string })?.message ?? 'Status update failed'}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-border">
          <div className="flex gap-2">
            {saved && saved.status !== 'delivered' && (
              <>
                {saved.status === 'preparing' && (
                  <button
                    onClick={() => patchStatusMutation.mutate('shipped')}
                    disabled={patchStatusMutation.isPending}
                    className="px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                  >
                    Mark shipped
                  </button>
                )}
                {saved.status === 'shipped' && (
                  <button
                    onClick={() => patchStatusMutation.mutate('in_transit')}
                    disabled={patchStatusMutation.isPending}
                    className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted disabled:opacity-50"
                  >
                    In transit
                  </button>
                )}
                {/* Recovery from a flagged exception (e.g. customs hold cleared):
                    the backend allows moving back to in-transit/delivered, so the
                    UI must offer a way out instead of stranding the shipment. */}
                {saved.status === 'exception' && (
                  <button
                    onClick={() => patchStatusMutation.mutate('in_transit')}
                    disabled={patchStatusMutation.isPending}
                    className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted disabled:opacity-50"
                  >
                    Resume (in transit)
                  </button>
                )}
                {(saved.status === 'shipped' || saved.status === 'in_transit' || saved.status === 'exception') && (
                  <button
                    onClick={() => patchStatusMutation.mutate('delivered')}
                    disabled={patchStatusMutation.isPending}
                    className="px-3 py-1.5 text-sm font-medium bg-emerald-600 text-white rounded-md disabled:opacity-50"
                  >
                    Mark delivered
                  </button>
                )}
                {saved.status !== 'exception' && (
                  <button
                    onClick={() => patchStatusMutation.mutate('exception')}
                    disabled={patchStatusMutation.isPending}
                    className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-muted disabled:opacity-50"
                  >
                    Flag exception
                  </button>
                )}
              </>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted">
              {saved ? 'Done' : 'Cancel'}
            </button>
            {!saved && unpaid && (
              <label className="flex items-center gap-2 text-xs text-amber-900 mr-auto">
                <input type="checkbox" checked={shipBeforePayment} onChange={(e) => setShipBeforePayment(e.target.checked)} />
                Ship before payment (proforma is {proforma.status})
              </label>
            )}
            {!saved ? (
              <button
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending || (unpaid && !shipBeforePayment)}
                className="px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
              >
                {createMutation.isPending ? 'Saving…' : 'Save shipment'}
              </button>
            ) : (
              <button
                onClick={() => patchFieldsMutation.mutate()}
                disabled={patchFieldsMutation.isPending}
                className="px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
              >
                {patchFieldsMutation.isPending ? 'Saving…' : 'Save changes'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
